import { TextFieldProps } from '@mui/material';
import { Button, Flex, IconButton, Input } from '@components';
import { FormattedMessage, useIntl } from 'react-intl';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  CategorizedDataField,
  NomineeCustomer,
} from '@features/api/response/NomineeCustomer';
import { useUpdateNomineeCustomerMutation } from '@features/api/nomineeCustomerSlice';
import { ChangeEvent, useState } from 'react';
import { CategoryContainer } from './CategorizedDataForm.styles';
import { InputProps } from '@components/Input/Input';
import NewItemDialog from '@pages/NomineeCustomers/NewCustomerForm/NewItemDialog';

export interface CategorizedDataFormProps {
  onSave: () => void;
  onEditCategoryName: (name: string) => void;
  customer: NomineeCustomer;
  categoryName: string;
}

function StyledInput({
  removeable = false,
  handleRemove,
  ...restProps
}: InputProps &
  TextFieldProps & { removeable?: boolean; handleRemove?: () => void }) {
  return (
    <Input
      {...restProps}
      InputProps={{
        sx: {
          flex: 1,
        },
        endAdornment: removeable ? (
          <IconButton
            onClick={handleRemove}
            size='small'
            iconBoxHeight={12}
            iconBoxWidth={12}
            icon={() => <DeleteOutlineIcon fontSize='small' color='error' />}
          />
        ) : null,
      }}
      sx={{
        flex: '0 49%',
      }}
    />
  );
}

export const CategorizedDataForm = ({
  onSave,
  customer,
  categoryName,
}: CategorizedDataFormProps) => {
  const intl = useIntl();

  const [updateMutation] = useUpdateNomineeCustomerMutation();

  const [newCategoryName, setNewCategoryName] = useState<string>(categoryName);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const [showFieldForm, setShowFieldForm] = useState(false);
  const [newFieldName, setNewFieldName] = useState<string>();

  const [data, setData] = useState<CategorizedDataField[]>(
    customer.categorizedData.find((x) => x.name === categoryName)?.fields ?? []
  );

  const handleSave = async () => {
    const updatedCategorizedData = [...customer.categorizedData];
    const updatedCategoryIndex = updatedCategorizedData.findIndex(
      (x) => x.name === categoryName
    );
    const updatedCategory = {
      name: updatedCategorizedData[updatedCategoryIndex].name,
      fields: data,
    };
    updatedCategorizedData[updatedCategoryIndex] = updatedCategory;

    await updateMutation({
      id: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      categorizedData: [...updatedCategorizedData],
    }).unwrap();

    onSave();
  };

  const handleRemoveField = (fieldIndex: number) => {
    if (data) {
      const updatedData = [...data];
      updatedData.splice(fieldIndex, 1);

      setData([...updatedData]);
    }
  };

  const handleChangeField = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldIndex: number
  ) => {
    const updatedData = [...data];
    updatedData[fieldIndex] = {
      ...updatedData[fieldIndex],
      value: event.target.value,
    };

    setData([...updatedData]);
  };

  const handleAddField = () => {
    setShowFieldForm(true);
    setNewFieldName(undefined);
  };

  const handleCancelAddField = () => {
    setShowFieldForm(false);
    setNewFieldName(undefined);
  };

  const handleCreateField = () => {
    if (!newFieldName) return;

    const updatedData = [...data];

    updatedData.push({
      name: newFieldName,
      value: '',
    });

    setData([...updatedData]);
    setShowFieldForm(false);
  };

  const handleNewFieldNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNewFieldName(event.target.value);
  };

  const handleDeleteCategory = async () => {
    const updatedCategorizedData = [...customer.categorizedData];
    const deletedCategoryIndex = updatedCategorizedData.findIndex(
      (x) => x.name === categoryName
    );
    updatedCategorizedData.splice(deletedCategoryIndex, 1);

    await updateMutation({
      id: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      categorizedData: [...updatedCategorizedData],
    }).unwrap();

    onSave();
  };

  const handleAddCategory = () => {
    setShowCategoryForm(true);
  };

  const handleCancelRenameCategory = () => {
    setShowCategoryForm(false);
  };

  const handleNewCategoryNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setNewCategoryName(event.target.value);
  };

  const handleRenameCategory = async () => {
    const updatedCategorizedData = [...customer.categorizedData];
    const categoryIndex = updatedCategorizedData.findIndex(
      (x) => x.name === categoryName
    );
    updatedCategorizedData[categoryIndex] = {
      name: newCategoryName,
      fields: updatedCategorizedData[categoryIndex].fields,
    };

    await updateMutation({
      id: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      categorizedData: [...updatedCategorizedData],
    }).unwrap();

    onSave();
  };

  return (
    <Flex flex={1} flexDirection='column' justifyContent='space-between'>
      <Flex flex={1} flexDirection='column' gap={10}>
        <CategoryContainer>
          <Flex flexWrap='wrap' gap={6}>
            {data.map((field, fieldIndex) => (
              <StyledInput
                key={field.name}
                label={field.name}
                defaultValue={field.value}
                onChange={(event) => handleChangeField(event, fieldIndex)}
                removeable
                handleRemove={() => handleRemoveField(fieldIndex)}
              />
            ))}
          </Flex>

          <Flex flex={1} alignItems='center' justifyContent='center'>
            <Button variant='tertiary' onClick={() => handleAddField()}>
              <FormattedMessage id='crm.customer.addField' />
            </Button>
          </Flex>
        </CategoryContainer>
      </Flex>

      <Flex mt={14} gap={8} justifyContent='space-between'>
        <Button
          data-testid='save-button'
          variant='primary'
          type='submit'
          onClick={handleSave}
        >
          <FormattedMessage id='misc.save' />
        </Button>
        <Flex gap={8} mr={18}>
          <Button
            data-testid='delete-button'
            variant='secondary'
            onClick={handleAddCategory}
          >
            <FormattedMessage id='misc.rename' />
          </Button>
          <Button
            data-testid='delete-button'
            variant='secondary'
            color='error'
            onClick={handleDeleteCategory}
          >
            <FormattedMessage id='misc.delete' />
          </Button>
        </Flex>
      </Flex>

      <NewItemDialog
        isOpen={showFieldForm}
        onClose={handleCancelAddField}
        label={intl.formatMessage({ id: 'crm.customer.fieldLabel' })}
        value={newFieldName}
        onChangeValue={handleNewFieldNameChange}
        onSubmit={handleCreateField}
        onCancel={handleCancelAddField}
      />

      <NewItemDialog
        isOpen={showCategoryForm}
        onClose={handleCancelRenameCategory}
        label={intl.formatMessage({ id: 'crm.customer.categoryName' })}
        value={newCategoryName}
        onChangeValue={handleNewCategoryNameChange}
        onSubmit={handleRenameCategory}
        onCancel={handleCancelRenameCategory}
      />
    </Flex>
  );
};
